import { Navigate, Link } from "react-router-dom";
import { useState } from "react";
import { LogOut, ExternalLink, LayoutDashboard, DollarSign, TrendingUp, Megaphone, ShieldAlert, Users, UserCog, UserCheck, Server, Download, ScrollText, Palette } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAdminAuth } from "@/context/AdminAuthContext";
import OverviewTab from "@/components/admin/OverviewTab";
import FinancesTab from "@/components/admin/FinancesTab";
import GrowthTab from "@/components/admin/GrowthTab";
import AdsTab from "@/components/admin/AdsTab";
import ModerationTab from "@/components/admin/ModerationTab";
import UsersTab from "@/components/admin/UsersTab";
import RealUsersTab from "@/components/admin/RealUsersTab";
import TeamTab from "@/components/admin/TeamTab";
import AuditTab from "@/components/admin/AuditTab";
import SiteEditorTab from "@/components/admin/SiteEditorTab";
import InfraTab from "@/components/admin/InfraTab";
import ExportTab from "@/components/admin/ExportTab";

const TABS = [
  { id: "overview",   label: "Resumen",     desc: "Métricas generales y últimos signups",            icon: LayoutDashboard, Component: OverviewTab,  perm: "overview:view" },
  { id: "finances",   label: "Finanzas",    desc: "MRR, propinas, anuncios, marketplace · PDF export", icon: DollarSign,      Component: FinancesTab,  perm: "finances:view" },
  { id: "growth",     label: "Crecimiento", desc: "DAU/MAU, signups por día, distribución por rol y país", icon: TrendingUp,  Component: GrowthTab,    perm: "growth:view" },
  { id: "ads",        label: "Anuncios",    desc: "Crear, pausar y eliminar campañas",                icon: Megaphone,       Component: AdsTab,       perm: "ads:view" },
  { id: "moderation", label: "Moderación",  desc: "Reportes de usuarios, contenido y baneos",         icon: ShieldAlert,     Component: ModerationTab, perm: "moderation:view" },
  { id: "users-real", label: "Usuarios",    desc: "Crear, editar, suspender y resetear contraseñas",  icon: UserCog,         Component: RealUsersTab, perm: "users:view" },
  { id: "team",       label: "Equipo Admin", desc: "Crear y gestionar admins · roles y permisos granulares", icon: UserCheck, Component: TeamTab,      perm: "team:view" },
  { id: "site",       label: "Editor visual", desc: "Cambiar tema, colores, textos y secciones del landing sin código", icon: Palette, Component: SiteEditorTab, perm: "site:view" },
  { id: "audit",      label: "Auditoría",   desc: "Bitácora de acciones administrativas",             icon: ScrollText,      Component: AuditTab,     perm: "audit:view" },
  { id: "users",      label: "Demos",       desc: "500 usuarios demo para llenar la plataforma",      icon: Users,           Component: UsersTab,     perm: "users:view" },
  { id: "infra",      label: "Ovamx Cloud", desc: "Estado de servidores, base de datos y rendimiento", icon: Server,         Component: InfraTab,     perm: "infra:view" },
  { id: "export",     label: "Exportar",    desc: "Descargar todas las colecciones en CSV / Excel / PDF", icon: Download,    Component: ExportTab,    perm: "export:run" },
];

export default function AdminDashboard() {
  const auth = useAdminAuth();
  const [active, setActive] = useState("overview");

  if (auth?.admin === null) return <div className="p-10 text-center text-ink-mid">Cargando…</div>;
  if (!auth?.admin || typeof auth.admin !== "object") return <Navigate to="/admin/login" replace />;

  // Filter tabs by effective permissions of the current admin
  const myPerms = new Set(auth.admin?.effective_permissions || []);
  const visibleTabs = TABS.filter((t) => !t.perm || myPerms.has(t.perm));
  const safeActive = visibleTabs.find((t) => t.id === active) ? active : (visibleTabs[0]?.id || "overview");
  const current = visibleTabs.find((t) => t.id === safeActive) || visibleTabs[0];
  const ActiveTab = current?.Component;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream text-ink">
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-xl border-b hairline">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Logo size={36} />
          <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.3em] text-coral border-l hairline pl-4 ml-1 truncate">
            Centro de Comando · {auth.admin?.email}
          </span>
          <Link to="/" className="ml-auto inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-coral transition link-underline">
            <ExternalLink className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Ver sitio</span>
          </Link>
          <button
            onClick={() => { auth.logout(); toast.success("Sesión cerrada"); }}
            data-testid="admin-logout"
            className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-red-700 transition"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Cerrar sesión</span>
          </button>
        </div>

        {/* Tab strip — sticky, scrollable horizontally on mobile */}
        <nav
          data-testid="admin-tabs"
          className="border-t hairline overflow-x-auto no-scrollbar"
        >
          <div className="max-w-[1400px] mx-auto px-2 md:px-6 flex gap-1 py-2 min-w-max">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const isActive = safeActive === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setActive(t.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-mono uppercase tracking-wider whitespace-nowrap transition shrink-0
                    ${isActive
                      ? "bg-ink text-cream shadow-md"
                      : "bg-cream-card hairline border text-ink-mid hover:text-ink hover:border-coral"}`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {!current ? (
          <div className="text-center py-16">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral mb-2">Sin permisos</p>
            <h1 className="font-serif text-4xl tracking-tighter">No tienes acceso a ningún módulo.</h1>
            <p className="mt-3 text-sm text-ink-mid">Pide al super administrador que asigne permisos a tu cuenta.</p>
          </div>
        ) : (
          <>
            <div className="mb-6 md:mb-8" data-testid={`admin-section-header-${current.id}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral inline-flex items-center gap-1.5">
                <current.icon className="w-3 h-3" /> {current.label}
              </p>
              <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tighter leading-none">
                {current.label}.
              </h1>
              <p className="mt-2 text-sm text-ink-mid">{current.desc}</p>
            </div>
            <ActiveTab />
          </>
        )}

        <p className="mt-12 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
          Ovamx · Centro de Comando v0.2 · tiempo real · {auth.admin?.email}
        </p>
      </main>
    </div>
  );
}
